import algoliasearch from 'algoliasearch/lite'
import { createAutocomplete } from '@algolia/autocomplete-core'
import { getAlgoliaResults } from '@algolia/autocomplete-preset-algolia'
import {
  FormControl,
  FormLabel,
  HStack,
  Input,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  StackDivider,
  Text,
  VStack,
  useDisclosure
} from '@chakra-ui/react'
import { useMemo, useState, useRef, useEffect, FC } from 'react'
import config from 'shared/config'
import get from 'lodash/get'

const searchClient = algoliasearch(config.algoliaAppId, config.algoliaSearchKey)

type Props = {
  title?: string
  onSelect: (id: string, name: string, email: string, phone: string) => void
}

const UsersSearch: FC<Props> = ({ title = 'Поиск', onSelect }) => {
  const { onOpen, onClose, isOpen } = useDisclosure()
  const inputRef = useRef<HTMLInputElement>(null)
  const [suggestions, setAutocompleteState] = useState([])

  useEffect(() => {
    suggestions.length > 0 ? onOpen() : onClose()
  }, [suggestions])

  const autocomplete = useMemo(
    () =>
      createAutocomplete({
        onStateChange ({ state }) {
          // (2) Synchronize the Autocomplete state with the React state.
          // @ts-ignore
          setAutocompleteState(get(state, ['collections', 0, 'items'], []))
        },
        // @ts-ignore
        getSources () {
          return [
            // (3) Use an Algolia index source.
            {
              sourceId: config.algoliaIndex,
              getItemInputValue ({ item }) {
                return item.query
              },
              getItems ({ query }) {
                return getAlgoliaResults({
                  searchClient,
                  queries: [
                    {
                      indexName: config.algoliaIndex,
                      query,
                      params: {
                        hitsPerPage: 12
                        // highlightPreTag: '<mark>',
                        // highlightPostTag: '</mark>'
                      }
                    }
                  ]
                })
              },
              getItemUrl ({ item }) {
                return item.url
              }
            }
          ]
        }
      }),
    []
  )

  const onSuggestionClick = (
    id: string,
    name: string,
    email: string,
    phone: string
  ) => {
    if (inputRef.current) {
      inputRef.current.value = ''
      setAutocompleteState([])
      onSelect(id, name, email, phone)
      autocomplete.setQuery('')
      onClose()
    }
  }

  const renderSuggestion = ({
    id,
    name,
    email,
    phone
  }: {
    id: string
    name: string
    email: string
    phone: string
  }) => {
    return (
      <HStack
        align={'flex-start'}
        w='full'
        key={id}
        _hover={{ bg: 'gray.50' }}
        cursor={'pointer'}
        onClick={() => onSuggestionClick(id, name, email, phone)}
      >
        <VStack flex={1} align='flex-start' w='full' spacing={0} flexShrink={0}>
          <Text color={'gray.800'} fontSize={'sm'} fontWeight={'semibold'}>
            {name}
          </Text>
          <Text color={'gray.600'} fontSize={'xs'} fontWeight={'medium'}>
            {email}
          </Text>
        </VStack>
        <VStack flex={1} align='flex-start' w='full' spacing={0} flexShrink={0}>
          <Text color={'gray.600'} fontSize={'xs'} fontWeight={'normal'}>
            {phone}
          </Text>
          <Text color={'gray.600'} fontSize={'xx-small'} fontWeight={'normal'}>
            {id}
          </Text>
        </VStack>
      </HStack>
    )
  }

  return (
    <FormControl w='full'>
      <FormLabel>{title}</FormLabel>
      <Popover
        returnFocusOnClose={false}
        isOpen={isOpen}
        onClose={onClose}
        placement='bottom-start'
        closeOnBlur={false}
        initialFocusRef={inputRef}
        matchWidth
      >
        <PopoverTrigger>
          <Input
            ref={inputRef}
            variant='flushed'
            placeholder='Имя, email, телефон или ID пользователя'
            type={'search'}
            value={
              autocomplete.getInputProps({ inputElement: inputRef.current })
                .value
            }
            // @ts-ignore
            onChange={
              autocomplete.getInputProps({ inputElement: inputRef.current })
                .onChange
            }
          />
        </PopoverTrigger>
        <PopoverContent w={'full'}>
          <PopoverBody w={'full'}>
            <VStack w={'full'} divider={<StackDivider color='gray.50' />}>
              {suggestions.map(renderSuggestion)}
            </VStack>
          </PopoverBody>
        </PopoverContent>
      </Popover>
    </FormControl>
  )
}

export default UsersSearch
